import { useState } from 'react';
import axios from 'axios';
import { Check, X, Clock, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Renders a status banner on the customer's booking card.
 * - awaiting_cleaner_response: "Waiting for cleaner to confirm…"
 * - change_proposed: shows the cleaner's proposed time + Accept/Decline buttons
 * - declined_by_cleaner: shows decline reason + refund notice
 * onResolved(): refreshes the parent's bookings.
 */
const CustomerBookingResponseBanner = ({ booking, onResolved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (booking.status === 'awaiting_cleaner_response') {
    return (
      <div
        className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-800 flex items-start gap-2"
        data-testid="awaiting-cleaner-banner"
      >
        <Clock className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Waiting for {booking.cleaner_name} to confirm</p>
          <p>You&apos;ll get a notification as soon as they accept, decline, or suggest a different time.</p>
        </div>
      </div>
    );
  }

  if (booking.status === 'declined_by_cleaner') {
    return (
      <div
        className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2"
        data-testid="declined-banner"
      >
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">{booking.cleaner_name} declined this booking</p>
          {booking.cleaner_decline_reason ? (
            <p className="italic mt-0.5">&ldquo;{booking.cleaner_decline_reason}&rdquo;</p>
          ) : null}
          <p className="mt-1">Your refund is being processed. You can book another cleaner anytime.</p>
        </div>
      </div>
    );
  }

  if (booking.status === 'change_proposed' && booking.proposed_changes) {
    const pc = booking.proposed_changes;

    const respond = async (action) => {
      setBusy(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/bookings/${booking.id}/customer-respond-change`,
          { action },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onResolved && onResolved();
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not submit response');
      } finally {
        setBusy(false);
      }
    };

    return (
      <div
        className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs space-y-2"
        data-testid="proposed-change-banner"
      >
        <div className="flex items-start gap-2 text-amber-800">
          <Clock className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-0.5">{booking.cleaner_name} suggested a different time</p>
            <p>
              Was: {booking.service_date} {booking.service_time}
            </p>
            <p>
              Proposed:{' '}
              <span className="font-semibold">
                {pc.new_service_date} {pc.new_start_time}–{pc.new_end_time}
              </span>
            </p>
            {pc.note && <p className="italic mt-0.5">&ldquo;{pc.note}&rdquo;</p>}
          </div>
        </div>
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex gap-2">
          <button
            onClick={() => respond('accept')}
            disabled={busy}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold px-3 py-1.5 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
            data-testid="accept-change-button"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Accept change
          </button>
          <button
            onClick={() => respond('decline')}
            disabled={busy}
            className="flex-1 bg-surface border border-red-300 text-red-700 hover:bg-red-50 font-semibold px-3 py-1.5 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
            data-testid="decline-change-button"
          >
            <X className="w-3 h-3" />
            Decline (cancel)
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default CustomerBookingResponseBanner;
